import type { JSX } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { useAuthPrompt, type AuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useIsGDPRDevice, useIsUKGDPRDevice } from '@/hooks/user';
import genericMessages from '@/messages/messages';

import { Wrapper } from './styles';
import { AuthTermsProps } from './types';

const AuthTerms = ({ signupTermsText, loginTermsText, lightText }: AuthTermsProps) => {
	const { formatMessage } = useIntl();
	const { authPrompt } = useAuthPrompt();
	const isUKGDPRDevice = useIsUKGDPRDevice();
	const isGDPRDevice = useIsGDPRDevice();
	const useShortTerms = isGDPRDevice || isUKGDPRDevice;
	const { logEvent } = useAnalytics();

	const getDefaultTermsMessages = (): {
		[mode in AuthPrompt]: {
			[isShort in 'short' | 'normal']: MessageDescriptor;
		};
	} => {
		return {
			signup: {
				normal: genericMessages.signupTermsV2,
				short: genericMessages.signupTermsShortV2,
			},
			login: {
				normal: genericMessages.loginTermsV2,
				short: genericMessages.loginTermsV2,
			},
		};
	};

	const defaultTermsMessage = formatMessage(
		getDefaultTermsMessages()[authPrompt][useShortTerms ? 'short' : 'normal'],
		{
			terms: (
				<a
					key="auth-terms-terms"
					href="/terms"
					target="_blank"
					onClick={() =>
						logEvent({
							eventName: 'Login Form : Terms : Clicked',
							eventProps: {
								mode: authPrompt,
							},
						})
					}
				>
					{formatMessage(genericMessages.terms)}
				</a>
			),
			privacypolicylink: (...chunks): JSX.Element => (
				<a
					key="auth-terms-privacy"
					href="/privacy"
					target="_blank"
					onClick={() =>
						logEvent({
							eventName: 'Login Form : Privacy Policy : Clicked',
							eventProps: {
								mode: authPrompt,
							},
						})
					}
				>
					{chunks}
				</a>
			),
		},
	);
	const customTermsMessages = authPrompt === 'login' ? loginTermsText : signupTermsText;

	const termsMessage = customTermsMessages ?? defaultTermsMessage;

	return (
		<Wrapper className="loginTerms" lightText={lightText}>
			{termsMessage}
		</Wrapper>
	);
};

export default AuthTerms;
